import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useDashboardTracking } from '../../hooks/useDashboardTracking';
import DateTimePickerInput from '../../components/DateTimePickerInput';

// Chart components
import TicketTypePieChart from './components/charts/TicketTypePieChart';
import StateBarChart from './components/charts/StateBarChart';
import TrendLineChart from './components/charts/TrendLineChart';
import PriorityDonutChart from './components/charts/PriorityDonutChart';
import SLAComplianceGauge from './components/charts/SLAComplianceGauge';
import CustomerGroupMatrixTable from './components/CustomerGroupMatrixTable';
import UnifiedPulseReportTable from './components/UnifiedPulseReportTable';

// Utils
import { DurationPreset, durationPresets, convertPresetToTimeRange, getDurationLabel, getDurationPresetRange } from './utils/dateHelpers';
import { generateDashboardPDF } from './utils/pdfExport';

interface Customer {
  id: string;
  accountName: string;
}

interface Group {
  id: string;
  name: string;
}

interface TrendDataPoint {
  date: string;
  cases: number;
  incidents: number;
  serviceRequests: number;
  changes: number;
  problems: number;
}

interface ChartFilter {
  visible: boolean;
  type: 'ticketType' | 'state' | 'priority' | 'date' | 'slaBreached' | 'trend' | 'matrix' | null;
  value: string | null;
  label: string;
  trendData?: TrendDataPoint[];
}

interface MatrixRow {
  customerId: string;
  customerName: string;
  data: { groupId: string; groupName: string; count: number }[];
}

interface MatrixData {
  customers: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  matrix: MatrixRow[];
  maxCount: number;
}

const CustomerWiseDashboard: React.FC = () => {
  // Subscribe to real-time updates via SSE for all ticket types
  useTicketEvents({ ticketTypes: ['INCIDENT', 'PROBLEM', 'CHANGE', 'CASE', 'SERVICE_REQUEST'] });

  // Get date formatter hook
  const { formatTime } = useDateFormatter();

  // Filter state
  const [customerId, setCustomerId] = useState<string>('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('this_month');
  const [previousPreset, setPreviousPreset] = useState<DurationPreset>('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [assignmentGroupIds, setAssignmentGroupIds] = useState<string[]>([]);

  // Chart interaction state
  const [chartFilter, setChartFilter] = useState<ChartFilter>({
    visible: false,
    type: null,
    value: null,
    label: '',
  });

  // Handle duration preset change
  const handleDurationChange = (newPreset: DurationPreset) => {
    if (newPreset === 'custom') {
      // Pre-populate custom dates based on the previous preset's range
      const range = getDurationPresetRange(previousPreset);
      setCustomDateFrom(range.from);
      setCustomDateTo(range.to);
      // Don't set applied dates yet - wait for Apply button
    } else {
      // For non-custom presets, update the previous preset tracker
      setPreviousPreset(newPreset);
      // Clear custom date state
      setCustomDateFrom('');
      setCustomDateTo('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
    }
    setDurationPreset(newPreset);
  };

  // Compute time range for API - uses applied dates for custom range
  const timeRange = useMemo(() => {
    if (durationPreset === 'custom') {
      // Only return custom range if dates have been applied
      if (appliedDateFrom && appliedDateTo) {
        return convertPresetToTimeRange('custom', appliedDateFrom, appliedDateTo);
      }
      // Keep using the previous preset's range until Apply is clicked
      return convertPresetToTimeRange(previousPreset);
    }
    return convertPresetToTimeRange(durationPreset);
  }, [durationPreset, appliedDateFrom, appliedDateTo, previousPreset]);

  // Handle applying custom date range
  const handleApplyCustomDates = () => {
    if (customDateFrom && customDateTo) {
      setAppliedDateFrom(customDateFrom);
      setAppliedDateTo(customDateTo);
    }
  };

  // Check if custom dates have been modified and need to be applied
  const customDatesNeedApply = durationPreset === 'custom' &&
    customDateFrom && customDateTo &&
    (customDateFrom !== appliedDateFrom || customDateTo !== appliedDateTo);

  // Track dashboard filters for Application Insights
  useDashboardTracking('CustomerWise', {
    customerId,
    durationPreset,
    assignmentGroupIds,
    customDateFrom: appliedDateFrom || undefined,
    customDateTo: appliedDateTo || undefined,
  });

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || response.data || [];
    },
  });

  // Fetch all assignment groups for filtering
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['all-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment&limit=500');
      return response.data.groups || response.data || [];
    },
  });

  // Get user's timezone offset in minutes (e.g., IST = -330, PST = 480)
  const timezoneOffset = new Date().getTimezoneOffset();

  // Fetch dashboard data with fresh data on every mount/tab focus
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['customer-wise-dashboard', customerId, timeRange, assignmentGroupIds, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupIds.length > 0) params.append('assignmentGroupId', assignmentGroupIds.join(','));
      // Send timezone offset so backend can aggregate by local date
      params.append('timezoneOffset', String(timezoneOffset));

      const response = await api.get(`/dashboard/customer-wise?${params}`);
      return response.data;
    },
    staleTime: 0,               // Always fetch fresh data on mount
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always',   // Always refetch on component mount
  });

  // Fetch matrix data for heatmap
  const { data: matrixData } = useQuery<MatrixData>({
    queryKey: ['customer-wise-matrix', customerId, timeRange, assignmentGroupIds],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupIds.length > 0) params.append('assignmentGroupId', assignmentGroupIds.join(','));

      const response = await api.get(`/dashboard/customer-wise/matrix?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: true,
  });

  // Handle chart clicks
  const handleTicketTypeClick = (ticketType: string) => {
    const typeLabels: Record<string, string> = {
      CASE: 'Cases',
      INCIDENT: 'Incidents',
      SERVICE_REQUEST: 'Service Requests',
      CHANGE: 'Changes',
      PROBLEM: 'Problems',
    };
    setChartFilter({
      visible: true,
      type: 'ticketType',
      value: ticketType,
      label: typeLabels[ticketType] || ticketType,
    });
  };

  const handleStateClick = (state: string) => {
    const stateLabels: Record<string, string> = {
      new: 'New',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      resolved: 'Resolved',
      closed: 'Closed',
      cancelled: 'Cancelled',
    };
    setChartFilter({
      visible: true,
      type: 'state',
      value: state,
      label: `State: ${stateLabels[state] || state}`,
    });
  };

  const handlePriorityClick = (priority: string) => {
    const priorityLabels: Record<string, string> = {
      critical_1: 'Critical',
      high_2: 'High',
      moderate_3: 'Moderate',
      low_4: 'Low',
      planning_5: 'Planning',
    };
    setChartFilter({
      visible: true,
      type: 'priority',
      value: priority,
      label: `Priority: ${priorityLabels[priority] || priority}`,
    });
  };

  const handleTrendPointClick = (_date: string) => {
    // Show trend summary table when clicking on the chart
    setChartFilter({
      visible: true,
      type: 'trend',
      value: null,
      label: 'Ticket Trend by Date',
      trendData: dashboardData?.trend || [],
    });
  };

  // Handle clicking on a specific cell in the trend table

  const handleSlaClick = (slaBreached: boolean) => {
    setChartFilter({
      visible: true,
      type: 'slaBreached',
      value: slaBreached ? 'true' : 'false',
      label: slaBreached ? 'SLA Breached' : 'SLA Met',
    });
  };

  const handleMatrixCellClick = (matrixCustomerId: string, groupId: string, customerName: string, groupName: string) => {
    setChartFilter({
      visible: true,
      type: 'matrix',
      value: `${matrixCustomerId}|${groupId}`,
      label: `${customerName} - ${groupName}`,
    });
  };

  const handleCloseTable = () => {
    setChartFilter({ visible: false, type: null, value: null, label: '' });
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!dashboardData) return;

    try {
      const customerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';
      const durationLabel = getDurationLabel(durationPreset, customDateFrom, customDateTo);
      const groupNames = assignmentGroupIds.length > 0
        ? groups.filter(g => assignmentGroupIds.includes(g.id)).map(g => g.name)
        : [];

      await generateDashboardPDF(dashboardData, customerName, durationLabel, groupNames, matrixData);
    } catch (err) {
      console.error('PDF Export failed:', err);
    }
  };

  const selectedCustomerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName}
                </option>
              ))}
            </select>
          </div>

          {/* Duration Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={durationPreset}
              onChange={(e) => handleDurationChange(e.target.value as DurationPreset)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {durationPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Group Filter - Always visible */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group</label>
            <select
              value={assignmentGroupIds[0] || ''}
              onChange={(e) => setAssignmentGroupIds(e.target.value ? [e.target.value] : [])}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export PDF Button and Refresh */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!dashboardData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Last Updated Indicator */}
        {dataUpdatedAt && (
          <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
            {isFetching && (
              <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            )}
            <span>
              Last updated: {formatTime(new Date(dataUpdatedAt))}
            </span>
          </div>
        )}

        {/* Custom Date Range (shown when custom is selected) */}
        {durationPreset === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <DateTimePickerInput
                value={customDateFrom}
                onChange={(val) => setCustomDateFrom(val)}
                showTimeSelect={true}
                className="w-full"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DateTimePickerInput
                  value={customDateTo}
                  onChange={(val) => setCustomDateTo(val)}
                  showTimeSelect={true}
                  className="w-full"
                />
              </div>
              <button
                onClick={handleApplyCustomDates}
                disabled={!customDateFrom || !customDateTo}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  customDatesNeedApply
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load dashboard data. Please try again.
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !isLoading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-gray-50 transition-all"
              onClick={() => setChartFilter({ visible: true, type: 'ticketType', value: '', label: 'All Tickets' })}
            >
              <div className="text-3xl font-bold text-gray-900">{dashboardData.summary.totalTickets}</div>
              <div className="text-sm text-gray-500">Total Tickets</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-purple-50 transition-all"
              onClick={() => handleTicketTypeClick('CASE')}
            >
              <div className="text-3xl font-bold text-purple-600">{dashboardData.summary.totalCases || 0}</div>
              <div className="text-sm text-gray-500">Cases</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-red-50 transition-all"
              onClick={() => handleTicketTypeClick('INCIDENT')}
            >
              <div className="text-3xl font-bold text-red-600">{dashboardData.summary.totalIncidents}</div>
              <div className="text-sm text-gray-500">Incidents</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-blue-50 transition-all"
              onClick={() => handleTicketTypeClick('SERVICE_REQUEST')}
            >
              <div className="text-3xl font-bold text-blue-600">{dashboardData.summary.totalServiceRequests}</div>
              <div className="text-sm text-gray-500">Service Requests</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-green-50 transition-all"
              onClick={() => handleTicketTypeClick('CHANGE')}
            >
              <div className="text-3xl font-bold text-green-600">{dashboardData.summary.totalChanges}</div>
              <div className="text-sm text-gray-500">Changes</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-amber-50 transition-all"
              onClick={() => handleTicketTypeClick('PROBLEM')}
            >
              <div className="text-3xl font-bold text-amber-600">{dashboardData.summary.totalProblems}</div>
              <div className="text-sm text-gray-500">Problems</div>
            </div>
            <div
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-lg hover:bg-red-50 transition-all"
              onClick={() => handleSlaClick(true)}
            >
              <div className="text-3xl font-bold text-red-600">{dashboardData.summary.slaBreached}</div>
              <div className="text-sm text-gray-500">SLA Breached</div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Ticket Type Pie Chart */}
            <TicketTypePieChart
              data={dashboardData.byTicketType}
              onSegmentClick={handleTicketTypeClick}
            />

            {/* State Bar Chart */}
            <StateBarChart
              data={dashboardData.byState}
              onBarClick={handleStateClick}
            />

            {/* SLA Compliance Gauge */}
            <SLAComplianceGauge
              slaCompliance={dashboardData.summary.slaCompliance}
              slaMet={dashboardData.summary.slaMet}
              slaBreached={dashboardData.summary.slaBreached}
              slaBreachedPercent={dashboardData.summary.slaBreachedPercent}
              totalSlaApplied={dashboardData.summary.totalSlaApplied}
              onSlaClick={handleSlaClick}
            />
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Priority Donut Chart */}
            <PriorityDonutChart
              data={dashboardData.byPriority}
              onSegmentClick={handlePriorityClick}
            />

            {/* Trend Line Chart */}
            <TrendLineChart
              data={dashboardData.trend}
              onPointClick={handleTrendPointClick}
            />
          </div>

          {/* Customer-Assignment Group Matrix Table */}
          {matrixData && matrixData.matrix.length > 0 && (
            <CustomerGroupMatrixTable
              matrix={matrixData.matrix}
              groups={matrixData.groups}
              maxCount={matrixData.maxCount}
              onCellClick={handleMatrixCellClick}
            />
          )}

          {/* Filtered Tickets Table */}
          <UnifiedPulseReportTable
            visible={chartFilter.visible && chartFilter.type !== 'trend'}
            filterLabel={chartFilter.label}
            baseFilters={{
              customerId,
              timeRange,
              assignmentGroupId: assignmentGroupIds.join(','),
              timezoneOffset,
            }}
            chartFilters={{
              ticketType: chartFilter.type === 'ticketType' ? (chartFilter.value as any) : undefined,
              state: chartFilter.type === 'state' ? chartFilter.value || undefined : undefined,
              priority: chartFilter.type === 'priority' ? chartFilter.value || undefined : undefined,
              date: chartFilter.type === 'date' ? chartFilter.value || undefined : undefined,
              slaBreached: chartFilter.type === 'slaBreached' ? chartFilter.value || undefined : undefined,
              filterType: chartFilter.type === 'matrix' ? 'matrix' : undefined,
              filterValue: chartFilter.type === 'matrix' ? chartFilter.value || undefined : undefined,
            }}
            onClose={handleCloseTable}
            ticketType={chartFilter.type === 'ticketType' ? (chartFilter.value as any) : undefined}
            customerName={selectedCustomerName}
          />
        </>
      )}
    </div>
  );
};

export default CustomerWiseDashboard;
